import * as React from "react";
import Box from "../parts/Box";
import { Helmet } from "react-helmet";
import { ll_url } from "../utils/tools";
import { workflowImgPath, workflowImgWidth } from "../settings";

const link = (url: string) => <a href={url} target="_blank" rel="noreferrer">{url}</a>

export default function AcknowledgementPage() {

    return <div className="ll-help-content">

<Helmet>
    <title>Acknowledgements</title>
</Helmet>

<h1>Acknowledgements</h1>

<Box title="Workflow diagram">
    <img src={ll_url(workflowImgPath)} width={workflowImgWidth} style={{ display: 'block', margin: '0 auto' }} alt="Workflow diagram" />
</Box>

<Box title="Integrated methods, tools and databases">
    <h3>AlphaFold DB</h3>
    <ul>
        <li>The AlphaFold Protein Structure Database is a database of high-accuracy protein-structure predictions provided by AlphaFold.</li>
        <li>Reference: {link('https://academic.oup.com/nar/advance-article/doi/10.1093/nar/gkab1061/6430488')}</li>
        <li>Homepage: {link('https://alphafold.ebi.ac.uk/')}</li>
    </ul>

    <h3>AutoDock Vina</h3>
    <ul>
        <li>AutoDock Vina is an open-source program for molecular docking and virtual screening.</li>
        <li>Reference: {link('https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3041641/')}</li>
        <li>Homepage: {link('http://vina.scripps.edu/')}</li>
    </ul>

    <h3>Clustal Omega</h3>
    <ul>
        <li>Clustal Omega is an accurate and very fast program for creating multiple alignments of protein and nucleotide sequences. It offers a significant increase in scalability over previous versions, allowing hundreds of thousands of sequences to be aligned in only a few hours. The accuracy of the program has been considerably improved over earlier Clustal programs, through the use of the HHalign method for aligning profile hidden Markov models.</li>
        <li>Reference: {link('https://www.ncbi.nlm.nih.gov/pubmed/21988835')}</li>
        <li>Homepage: {link('http://www.clustal.org/omega/')}</li>
    </ul>

    <h3>DeepAlign</h3>
    <ul>
        <li>DeepAlign is a protein structure alignment method that combines geometric similarity with evolutionary and hydrogen-bonding information.</li>
        <li>Reference: {link('http://www.ncbi.nlm.nih.gov/pubmed/23486213')}</li>
        <li>Homepage: {link('https://github.com/realbigws/DeepAlign')}</li>
    </ul>

    <h3>DrugBank</h3>
    <ul>
        <li>DrugBank is the world’s largest online database of drug and drug target information.</li>
        <li>Reference: {link('https://www.ncbi.nlm.nih.gov/pubmed/29126136')}</li>
        <li>Homepage: {link('https://www.drugbank.com/')}</li>
    </ul>

    <h3>FoldX</h3>
    <ul>
        <li>FoldX is an empirical force field that was developed for the rapid evaluation of the effect of mutations on the stability, folding and dynamics of proteins and nucleic acids.</li>
        <li>Reference: {link('https://www.ncbi.nlm.nih.gov/pmc/articles/PMC1160148/')}</li>
        <li>Homepage: {link('http://foldxsuite.crg.eu/')}</li>
    </ul>

    <h3>HOPE</h3>
    <ul>
        <li>HOPE is a web service that analyses the structural effects of a point mutation in a protein sequence.</li>
        <li>Reference: {link('https://doi.org/10.1186/1471-2105-11-548')}</li>
        <li>Homepage: {link('https://www3.cmbi.umcn.nl/hope/')}</li>
    </ul>

    <h3>Jensen-Shannon divergence</h3>
    <ul>
        <li>Jensen-Shannon divergence algorithm calculates levels of evolutionary conservation for each amino acid position. The conservation scores are calculated based on the phylogenetic relationships between the target protein and its close homologs.</li>
        <li>Reference: {link('http://www.ncbi.nlm.nih.gov/pubmed/17519246')}</li>
        <li>Homepage: {link('https://compbio.cs.princeton.edu/conservation/')}</li>
    </ul>

    <h3>MODELLER</h3>
    <ul>
        <li>MODELLER is a tool for homology modelling of protein structures.</li>
        <li>Reference: {link('https://www.ncbi.nlm.nih.gov/pubmed/24573470')}</li>
        <li>Homepage: {link('https://salilab.org/modeller/')}</li>
    </ul>

    <h3>Mol*</h3>
    <ul>
        <li>Mol* (<i>/'molstar/</i>) is a modern web-based open-source toolkit for visualization and analysis of large-scale molecular data.</li>
        <li>Reference: {link('https://doi.org/10.1093/nar/gkab314')}</li>
        <li>Homepage: {link('https://molstar.org/')}</li>
    </ul>

    <h3>P2Rank</h3>
    <ul>
        <li>P2Rank is a tool for ligand binding site prediction from protein structure.</li>
        <li>Reference: {link('https://doi.org/10.1186/s13321-018-0285-8')}</li>
        <li>Homepage: {link('https://p2rank.cz/')}</li>
    </ul>

    {/* <h3>PIC</h3> */}

    <h3>PredictSNP</h3>
    <ul>
        <li>PredictSNP is a consensus classifier for prediction of the effect of the single point mutation on the protein function.</li>
        <li>Reference: {link('https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3894168/')}</li>
        <li>Homepage: {link('https://loschmidt.chemi.muni.cz/predictsnp1/')}</li>
    </ul>

    <h3>PROPKA</h3>
    <ul>
        <li>PROPKA predicts the pKa values of ionizable groups in proteins.</li>
        <li>Reference: {link('https://doi.org/10.1021/ct100578z')}</li>
        <li>Homepage: {link('http://propka.org/')}</li>
    </ul>

    <h3>Rosetta</h3>
    <ul>
        <li>The Rosetta software suite includes algorithms for computational modeling and analysis of protein structures.</li>
        <li>Reference: {link('https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3760476/')}</li>
        <li>Homepage: {link('https://www.rosettacommons.org/software')}</li>
    </ul>

    <h3>SWISS-MODEL</h3>
    <ul>
        <li>SWISS-MODEL is a web-based integrated service dedicated to protein structure homology modelling.</li>
        <li>Reference: {link('https://doi.org/10.1093/nar/gky427')}</li>
        <li>Homepage: {link('https://swissmodel.expasy.org/')}</li>
    </ul>

    <h3>UniProt/Swiss-Prot</h3>
    <ul>
        <li>Swiss-Prot is a curated protein sequence database which strives to provide a high level of annotation (such as the description of the function of a protein, its domains structure, post-translational modifications, variants, etc.), a minimal level of redundancy and high level of integration with other databases.</li>
        <li>Reference: {link('https://www.ncbi.nlm.nih.gov/pubmed/25348405')}</li>
        <li>Homepage: {link('https://www.uniprot.org/')}</li>
    </ul>

    <h3>UniRef</h3>
    <ul>
        <li>The UniProt Reference Cluster (UniRef) databases provide clustered sets of sequences from UniProtKB and selected UniParc records. This hides redundant sequences and obtains complete coverage of the sequence space.</li>
        <li>Reference: {link('https://pubmed.ncbi.nlm.nih.gov/25398609/')}</li>
        <li>Homepage: {link('https://www.uniprot.org/uniref/')}</li>
    </ul>

    <h3>wwPDB</h3>
    <ul>
        <li>The Protein Data Bank (PDB) is the single global archive for 3D macromolecular structure data managed by the Worldwide Protein Data Bank (wwPDB).</li>
        <li>Reference: {link('https://doi.org/10.1093/nar/gky949')}</li>
        <li>Homepage: {link('https://wwpdb.org/')}</li>
    </ul>

    <h3>ZINC</h3>
    <ul>
        <li>ZINC is a free database of commercially-available compounds for virtual screening.</li>
        <li>Reference: {link('http://pubs.acs.org/doi/abs/10.1021/acs.jcim.5b00559')}</li>
        <li>Homepage: {link('https://zinc.docking.org/')}</li>
    </ul>

</Box>

<Box title="Funding">
    <p style={{ margin: '0.5rem 0' }}>Supported by the project National institute for cancer research (No LX22NPO5102) – Funded by the European Union – Next Generation EU.</p>
</Box>

    </div>
}
